import { GetListParams } from 'react-admin';
import { CustomDataProvider } from '../../providers/CustomDataProvider';
import { Resources } from '../resource';
import { apiGet, apiSend } from '../perps-v1-shared/api';
import { AdminMarket, DisplayText, SymbolDisplayMeta } from '../perps-v1-shared/types';

// GET /perps-v1/symbols 返回的行 = AdminMarket 基础字段 + 可选的展示资料覆盖（logoUrl/name）
export interface SymbolRow extends AdminMarket {
  logoUrl?: string | null;
  name?: DisplayText | null;
}

export interface SymbolSectorTag {
  code: string;
}

export interface UpsertDisplayInput {
  symbol: string;
  logoUrl?: string | null;
  name?: DisplayText;
}

export interface ImportDisplayEntry {
  symbol: string;
  logoUrl?: string;
  name: { en?: string; zh?: string };
}

export interface ImportDisplayResult {
  count: number;
}

// POST /perps-v1/symbols/sync-icons response: every non-delisted symbol lands in exactly one bucket
export interface SyncIconsResult {
  filled: number;
  skippedCurated: number;
  unavailable: number;
}

// GET /perps-v1/symbols/membership 中的一行：一个 view 可能出现两次（一条 manual + 一条 rule）
export interface SymbolMembershipRow {
  id: string;
  viewCode: string;
  source: 'manual' | 'rule';
}

export interface SymbolMembershipResponse {
  data: SymbolMembershipRow[];
  total: number;
}

// getList 需要额外的展示资料（logoUrl/name），/perps-v1/symbols 本身不带这两个字段，
// 因此在拿到行情分页后再拉一次 display 列表按 symbol 合并；display 拉取失败时降级为无展示资料，
// 不影响主列表渲染，仅对外抛出一次错误供上层 notify。
type DisplayMergeErrorListener = (error: unknown) => void;
const displayMergeErrorListeners = new Set<DisplayMergeErrorListener>();

export function onDisplayMergeError(listener: DisplayMergeErrorListener): () => void {
  displayMergeErrorListeners.add(listener);
  return () => {
    displayMergeErrorListeners.delete(listener);
  };
}

function emitDisplayMergeError(error: unknown) {
  displayMergeErrorListeners.forEach((listener) => listener(error));
}

// Shared "give me everything" pager for the /symbols and /symbols/display list endpoints — both
// speak the same { data, total, page, size } contract. Fetches one page of `pageSize` up front;
// if the server's `total` says there's more than that page returned (whether because the caller
// asked for more rows than exist per page, or because the endpoint silently clamps `size` below
// what was requested), it keeps requesting subsequent pages — sized off what the server actually
// handed back, not off what was asked for — until every row is collected. A page that comes back
// empty stops the loop so a misbehaving endpoint can't spin it forever.
async function fetchAllPages<T>(
  buildPath: (page: number, size: number) => string,
  pageSize = 1000
): Promise<{ data: T[]; total: number }> {
  const first = await apiGet<{ data: T[]; total: number; page: number; size: number }>(
    buildPath(1, pageSize)
  );
  const data: T[] = [...(first?.data ?? [])];
  const total = first?.total ?? data.length;
  let page = 2;
  while (data.length < total) {
    // Genuinely sequential: how many more pages are needed depends on `total`/`data.length`, both
    // only known after the previous request resolves — there is nothing here to parallelize.
    // eslint-disable-next-line no-await-in-loop
    const next = await apiGet<{ data: T[]; total: number; page: number; size: number }>(
      buildPath(page, first?.size || pageSize)
    );
    const batch = next?.data ?? [];
    if (batch.length === 0) break;
    data.push(...batch);
    page += 1;
  }
  return { data, total };
}

// Loads every display profile, not just the first page. GET /perps-v1/symbols/display orders by
// symbol ASC, has no server-side clamp on `size` (only a `< 1 -> 50` floor), and returns `total` —
// so following `total` past one page with additional page fetches (fetchAllPages above) is exact,
// not a guess. This matters beyond the missingLogo filter below: the merge onto `rows` happens for
// every getList call, so a profile past the first page previously merged as "no logo" for every
// caller once profile count passed 1000. This feature creates profiles hourly, so that count trends
// toward the symbol count and the old single-page fetch would have started silently mis-rendering
// logos across the whole list, not just under the filter.
async function fetchDisplayMetaBySymbol(): Promise<Map<string, { logoUrl?: string | null; name?: DisplayText | null }>> {
  const map = new Map<string, { logoUrl?: string | null; name?: DisplayText | null }>();
  try {
    const { data } = await fetchAllPages<SymbolDisplayMeta>(
      (page, size) => `/perps-v1/symbols/display?page=${page}&size=${size}`
    );
    data.forEach((item) => {
      map.set(item.symbol, { logoUrl: item.logoUrl, name: item.name });
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('PerpsV1SymbolsDataProvider display merge error:', error);
    emitDisplayMergeError(error);
  }
  return map;
}

// Datagrid columns wired to a `source`/sortable=true (Symbol, 24h volume) — must match the API's
// sortBy vocabulary (`symbol` | `volume24h`) verbatim, see dataProvider.getList below.
const SORTABLE_LIST_FIELDS = new Set(['symbol', 'volume24h']);

// getList -> GET /perps-v1/symbols（服务端分页 + 过滤）+ GET /perps-v1/symbols/display（合并展示资料）；
// 其余动作走 api.ts 直连 body-based 接口
export class PerpsV1SymbolsDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    if (resource !== Resources.perpsV1Symbols) {
      return Promise.reject(new Error('Unsupported getList resource'));
    }
    const { page = 1, perPage = 10 } = params.pagination || {};
    const filter: Record<string, unknown> = params.filter || {};
    const isMissingLogoFilter = filter.missingLogo === true;

    // Filter params only — page/size are layered on separately below, because the missingLogo
    // path needs every matching row (fetchAllPages), not just the UI's current page.
    const filterQuery = new URLSearchParams();
    if (filter.search) filterQuery.set('search', String(filter.search));
    // dex 过滤：UI 用 'native' 表示原生币种，后端约定原生 dex 为空字符串
    if (filter.dex === 'native') {
      filterQuery.set('dex', '');
    } else if (filter.dex) {
      filterQuery.set('dex', String(filter.dex));
    }
    if (filter.sector) filterQuery.set('sector', String(filter.sector));
    if (filter.untaggedOnly === true) filterQuery.set('untaggedOnly', 'true');
    if (filter.includeDelisted === true) filterQuery.set('includeDelisted', 'true');

    // react-admin always supplies params.sort (defaults to { field: 'id', order: 'ASC' } before the
    // admin picks a column) — only forward sortBy/sortDirection when it names a column the API can
    // actually sort on (GET /perps-v1/symbols rejects anything else with a 502); otherwise omit both
    // so the API's natural order is preserved.
    if (params.sort && SORTABLE_LIST_FIELDS.has(params.sort.field)) {
      filterQuery.set('sortBy', params.sort.field);
      filterQuery.set('sortDirection', params.sort.order === 'DESC' ? 'DESC' : 'ASC');
    }

    const symbolsPath = (p: number, size: number) => {
      const q = new URLSearchParams(filterQuery);
      q.set('page', String(p));
      q.set('size', String(size));
      return `/perps-v1/symbols?${q.toString()}`;
    };

    // "Missing logo" has no server-side equivalent (GET /perps-v1/symbols doesn't take a logo
    // filter, and hasDisplayMeta on that row is NOT "has a logo" — a profile can carry only
    // localized names). Filtering it client-side against just the UI's current page would make the
    // pagination footer lie: e.g. 340 symbols with 8 missing logos all on later pages would show
    // page 1 as empty while the footer still reads "1-25 of 340", reading as "nothing missing"
    // when 8 symbols still need a logo. So when this toggle is on, pull every symbol matching the
    // other filters (fetchAllPages, same "no clamp is assumed — follow `total`" pattern as
    // fetchDisplayMetaBySymbol above), filter the full set, and only then paginate client-side —
    // `total` below becomes the true filtered count, not the server's unfiltered one.
    const [symbolsResult, displayBySymbol] = await Promise.all([
      isMissingLogoFilter
        ? fetchAllPages<SymbolRow>(symbolsPath)
        : apiGet<{ data: SymbolRow[]; page: number; size: number; total: number }>(
            symbolsPath(page, perPage)
          ).then((json) => ({ data: json?.data ?? [], total: json?.total ?? 0 })),
      fetchDisplayMetaBySymbol(),
    ]);

    const rows = symbolsResult.data.map((row) => {
      const meta = displayBySymbol.get(row.symbol);
      return meta ? { ...row, logoUrl: meta.logoUrl, name: meta.name } : row;
    });

    if (isMissingLogoFilter) {
      const filtered = rows.filter((row) => !row.logoUrl);
      const start = (page - 1) * perPage;
      return { data: filtered.slice(start, start + perPage), total: filtered.length };
    }

    return { data: rows, total: symbolsResult.total };
  }
}

export const perpsV1SymbolsDataProvider = new PerpsV1SymbolsDataProvider();

export const symbolsApi = {
  setTags: (symbol: string, tags: SymbolSectorTag[]) =>
    apiSend<AdminMarket>('/perps-v1/symbols/tags', 'PUT', { symbol, tags }),
  unlockTags: (symbol: string) => apiSend<AdminMarket>('/perps-v1/symbols/tags/unlock', 'PUT', { symbol }),
  upsertDisplay: (input: UpsertDisplayInput) =>
    apiSend<SymbolDisplayMeta>('/perps-v1/symbols/display', 'PUT', input),
  importDisplay: (entries: ImportDisplayEntry[]) =>
    apiSend<ImportDisplayResult>('/perps-v1/symbols/display/import', 'POST', { entries }),
  deleteDisplay: (symbol: string) =>
    apiSend<{ deleted: boolean }>('/perps-v1/symbols/display', 'DELETE', { symbol }),
  // Empty body — operator comes from the JWT, not the request.
  syncIcons: () => apiSend<SyncIconsResult>('/perps-v1/symbols/sync-icons', 'POST'),
};

// PUT /membership 是 manual 挂载的全量覆盖（不动 rule 命中/exclude），响应体是刷新后的完整 membership 列表
export const membershipApi = {
  get: (symbol: string) =>
    apiGet<SymbolMembershipResponse>(`/perps-v1/symbols/membership?symbol=${encodeURIComponent(symbol)}`),
  save: (symbol: string, viewCodes: string[]) =>
    apiSend<SymbolMembershipResponse>('/perps-v1/symbols/membership', 'PUT', { symbol, viewCodes }),
};
